/**
 * complianceModel — plain-language reading of a change-tracker item for the Risk panel's Compliance reviews
 * (Bill 2026-11-04: "Everyone (Reid, Peter, Anthony, Jessica, examiners) must instantly understand each item: what it
 * is, why, what was done, what hasn't been done, why.").
 *
 * Pure functions only (tested in __tests__/complianceModel.test.ts). Every sentence is composed from the item's own
 * fields, history and links; nothing is invented. Wording follows the Hub web part's reviewer screen
 * (pcb-innovation-hub TrackerItemDetail / trackerModel), with the tracker's code words translated.
 */
import type { ReviewAction, ReviewDetail, ReviewEvent, ReviewLink, ReviewRow } from '@/services/complianceReviewsApi';

const lc = (s: string | null | undefined): string => (s ?? '').trim().toLowerCase();

// ── people ───────────────────────────────────────────────────────────────────────────────────────────────────

/** "jessica.beucler@mypcb.com" → "Jessica Beucler"; "app:…" → "Automatic (the app)". */
export function personName(upn: string | null | undefined, name?: string | null): string {
  if (name && name.trim() && !name.includes('@') && !name.startsWith('app:')) return name.trim();
  const u = (upn ?? '').trim();
  if (!u) return '—';
  if (u.startsWith('app:')) return 'Automatic (the app)';
  const local = u.split('@')[0] ?? u;
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}

// ── vocabulary ───────────────────────────────────────────────────────────────────────────────────────────────

const STATUS_WORDS: Record<string, string> = {
  filed: 'Reported, not looked at yet',
  triaged: 'Being looked at',
  decided: 'Decided, not built yet',
  built: 'Built, not live yet',
  deployed: 'Live in the app',
  verified: 'Live and checked',
  'revert-requested': 'Undo requested',
  reverted: 'Undone',
  closed: 'Closed',
};

/** Where the item stands, in words. */
export function statusWords(status: string | null | undefined): string {
  return STATUS_WORDS[lc(status)] ?? (status || 'Unknown');
}

export type Tone = 'ok' | 'watch' | 'open' | 'problem' | 'neutral';

export interface FlagBadge {
  readonly label: string;
  readonly tone: Tone;
  readonly explain: string;
}

/** The compliance flag, in words. */
export function flagBadge(flag: string | null | undefined): FlagBadge {
  switch (lc(flag)) {
    case 'agent-flagged':
      return { label: 'Needs your review', tone: 'watch', explain: 'Flagged as possibly affecting compliance. No compliance reviewer has looked at it yet.' };
    case 'confirmed':
      return { label: 'Compliance impact confirmed', tone: 'problem', explain: 'A compliance reviewer confirmed this change affects compliance.' };
    case 'cleared':
      return { label: 'Cleared by compliance', tone: 'ok', explain: 'A compliance reviewer recorded that this change does not affect compliance.' };
    default:
      return { label: 'Not assessed', tone: 'neutral', explain: 'Not assessed for compliance.' };
  }
}

const DETERMINATION_WORDS: Record<string, string> = {
  fix: 'Fix it',
  change: 'Make the change',
  'no-change': 'No change needed',
  duplicate: 'Same as another request',
  declined: 'Declined, will not be changed',
  'not-reproducible': 'Could not be reproduced',
};

export function determinationWords(d: string | null | undefined): string {
  if (!d) return 'Not decided yet';
  return DETERMINATION_WORDS[lc(d)] ?? d;
}

const BUILDS = ['fix', 'change'];

const SOURCE_WORDS: Record<string, string> = {
  'life-ring': 'A staff member reported a problem with the life ring (the help button in the app)',
  development: 'Raised by the developers while building the platform',
  ask: 'Asked for through an Innovation Hub request',
  backlog: 'From the platform’s planned work list',
};

export function sourceWords(source: string | null | undefined): string {
  return SOURCE_WORDS[lc(source)] ?? (source || 'Unknown source');
}

const APP_WORDS: Record<string, string> = {
  ROP: 'Retail Operations Platform (account opening)',
  LWP: 'Loan Write-up',
  LOP: 'Loan Operations Platform',
};

export function platformWords(p: string | null | undefined): string {
  const code = (p ?? '').trim().toUpperCase();
  return APP_WORDS[code] ?? code ?? '—';
}

export function categoryWords(c: string | null | undefined): string {
  if (!c) return 'Not categorised yet';
  return c.charAt(0).toUpperCase() + c.slice(1);
}

const LINK_WORDS: Record<string, string> = {
  backlog: 'Planned work item',
  ask: 'Innovation Hub request',
  change: 'Release record',
  commit: 'Code change',
  version: 'Released in version',
  source: 'The original report',
};

export function linkWords(type: string): string {
  return LINK_WORDS[lc(type)] ?? type;
}

/** Where a link opens, if anywhere: a web address, or a code change "repo@sha" on PCB's GitHub. */
export function linkUrl(l: Pick<ReviewLink, 'type' | 'ref'>): string | null {
  const ref = l.ref.trim();
  if (/^https?:\/\//i.test(ref)) return ref;
  if (lc(l.type) === 'commit') {
    const m = /^([A-Za-z0-9._-]+)@([0-9a-f]{7,40})$/i.exec(ref);
    if (m) return `https://github.com/PhelpsCountyBank/${m[1]}/commit/${m[2]}`;
  }
  return null;
}

// ── dates ────────────────────────────────────────────────────────────────────────────────────────────────────

export function dayText(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function whenText(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** "today", "1 day", "12 days" since iso. */
export function waitedText(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const days = Math.floor((now.getTime() - d.getTime()) / 86_400_000);
  return days <= 0 ? 'today' : days === 1 ? '1 day' : `${days} days`;
}

// ── the briefing ─────────────────────────────────────────────────────────────────────────────────────────────

export interface BriefLine {
  readonly text: string;
  /** Optional plain detail under the line (a note, a reason). */
  readonly detail?: string;
  readonly url?: string | null;
}

export interface Briefing {
  /** What it is: the change in one sentence and where it came from. */
  readonly whatItIs: ReadonlyArray<BriefLine>;
  /** Why: the reason the change was asked for and the decision on it. */
  readonly why: ReadonlyArray<BriefLine>;
  /** Why it was sent to compliance. */
  readonly whyFlagged: string | null;
  /** What was done: each step taken, with dates, who, versions and code changes. */
  readonly done: ReadonlyArray<BriefLine>;
  /** What is not done yet, each with why it is not done. */
  readonly notDone: ReadonlyArray<BriefLine>;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);

/** Composes the briefing from the item's own fields, history and links. */
export function briefing(d: ReviewDetail, now: Date = new Date()): Briefing {
  const whatItIs: BriefLine[] = [];
  whatItIs.push({ text: d.oneLiner?.trim() || 'No one-line summary has been written yet.' });
  if (d.requestedChange?.trim()) whatItIs.push({ text: 'The change asked for', detail: d.requestedChange.trim() });
  whatItIs.push({
    text: `${sourceWords(d.source)}: ${personName(d.requestedBy, d.requestedByName)}, ${dayText(d.requestedAt)}.`,
  });

  const why: BriefLine[] = [];
  const intake = d.intake ?? {};
  const attempting = str(intake['attempting']);
  const actual = str(intake['actual']);
  const page = str(intake['page']);
  if (attempting) why.push({ text: 'What they were trying to do', detail: attempting });
  if (actual) why.push({ text: 'What happened instead', detail: actual });
  if (page) why.push({ text: 'Where in the app', detail: page });
  if (d.determination) {
    why.push({
      text: `Decision: ${determinationWords(d.determination)} (${personName(d.approver, d.approverName)}, ${dayText(d.decidedAt ?? d.approvedAt)}).`,
      detail: d.determinationNote?.trim() || undefined,
    });
  } else {
    why.push({ text: 'No decision has been made yet on whether to change anything.' });
  }

  const done: BriefLine[] = [];
  done.push({ text: `Reported ${dayText(d.filedAt ?? d.requestedAt)}.` });
  if (d.triagedAt) done.push({ text: `Looked at by the developers ${dayText(d.triagedAt)}.` });
  if (d.decidedAt) done.push({ text: `Decided ${dayText(d.decidedAt)}: ${determinationWords(d.determination)}.` });
  if (d.builtAt) done.push({ text: `Built ${dayText(d.builtAt)}.` });
  const versions = d.links.filter((l) => lc(l.type) === 'version').map((l) => l.label?.trim() || l.ref);
  if (d.deployedAt) {
    done.push({ text: `Released into the live app ${dayText(d.deployedAt)}${versions.length ? ` as version ${versions.join(', ')}` : ''}.` });
  } else if (versions.length) {
    done.push({ text: `Version ${versions.join(', ')} recorded.` });
  }
  if (d.verifiedAt) done.push({ text: `Checked working in the live app ${dayText(d.verifiedAt)}.` });
  for (const l of d.links.filter((x) => lc(x.type) === 'commit' || lc(x.type) === 'change')) {
    done.push({ text: `${linkWords(l.type)}: ${l.label?.trim() || l.ref}`, url: linkUrl(l) });
  }
  if (lc(d.complianceFlag) === 'confirmed' || lc(d.complianceFlag) === 'cleared') {
    done.push({
      text: `${flagBadge(d.complianceFlag).label} by ${personName(d.complianceReviewedBy)}, ${dayText(d.complianceReviewedAt)}.`,
      detail: d.complianceNote?.trim() || undefined,
    });
  }
  if (d.revertRequestedAt) {
    done.push({ text: `Undo requested by ${personName(d.revertRequestedBy)}, ${dayText(d.revertRequestedAt)}.`, detail: d.revertReason?.trim() || undefined });
  }
  if (d.revertedAt) done.push({ text: `Undone ${dayText(d.revertedAt)}.` });
  if (d.closedAt) done.push({ text: `Closed ${dayText(d.closedAt)}.` });

  return { whatItIs, why, whyFlagged: d.complianceRationale?.trim() || null, done, notDone: notDone(d, now) };
}

/** What has not happened yet, each with the reason it has not (from where the item stands). */
export function notDone(d: ReviewDetail, now: Date = new Date()): BriefLine[] {
  const out: BriefLine[] = [];
  const st = lc(d.status);
  const flag = lc(d.complianceFlag);
  if (flag === 'agent-flagged') {
    out.push({
      text: 'Compliance review',
      detail: `Waiting for a compliance reviewer (${waitedText(d.filedAt ?? d.requestedAt, now)} so far). The item cannot be closed until a reviewer confirms or clears it.`,
    });
  }
  if (st === 'closed' || st === 'reverted') return out;
  const builds = BUILDS.includes(lc(d.determination));
  if (!d.determination) {
    out.push({
      text: 'A decision',
      detail: st === 'filed' ? 'The developers have not looked at it yet.' : 'The developers are looking at it and have not decided whether to change anything.',
    });
    return out;
  }
  if (!builds) {
    out.push({ text: 'Nothing will be built', detail: `${determinationWords(d.determination)}. It stays open until it is closed.` });
    return out;
  }
  if (st === 'revert-requested') {
    out.push({ text: 'The undo', detail: 'An undo was requested and the developers have not undone the change yet.' });
    return out;
  }
  if (!d.builtAt && (st === 'decided' || st === 'triaged' || st === 'filed')) {
    out.push({ text: 'Building the change', detail: 'It was decided, but the developers have not built it yet.' });
  }
  if (!d.deployedAt) {
    out.push({ text: 'Release into the live app', detail: d.builtAt ? 'Built but not released yet.' : 'Comes after it is built.' });
  }
  if (!d.verifiedAt) {
    out.push({
      text: 'Checking it works in the live app',
      detail: d.deployedAt ? 'Released, but nobody has recorded checking it in the live app yet.' : 'Comes after it is released.',
    });
  }
  if (!d.closedAt) {
    out.push({
      text: 'Closing the item',
      detail: flag === 'agent-flagged' ? 'Waits for the compliance review.' : d.verifiedAt ? 'Checked; the developers have not closed it yet.' : 'Comes last.',
    });
  }
  return out;
}

// ── history ──────────────────────────────────────────────────────────────────────────────────────────────────

const EVENT_WORDS: Record<string, string> = {
  filed: 'Reported',
  mirrored: 'Updated from the original report',
  transition: 'Moved on',
  edited: 'Details edited',
  'note-corrected': 'Decision note corrected',
  'link-added': 'Link added',
  'review-confirm': 'Compliance impact confirmed',
  'review-clear': 'Cleared by compliance',
  'review-note': 'Reviewer note',
  'revert-requested': 'Undo requested',
  'revert-ask-filed': 'Undo request sent to the developers',
  'revert-ask-failed': 'Undo request could not be sent',
};

export interface HistoryLine {
  readonly id: number;
  readonly when: string;
  readonly who: string;
  readonly what: string;
  readonly note: string | null;
  readonly viaPanel: boolean;
}

function obj(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

/** One history entry in words, newest first. */
export function historyLines(events: ReadonlyArray<ReviewEvent>): HistoryLine[] {
  return [...events]
    .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt) || b.eventId - a.eventId)
    .map((e) => {
      const detail = obj(e.detail) ?? {};
      const after = obj(detail['after']) ?? {};
      let what = EVENT_WORDS[lc(e.eventType)] ?? e.eventType.replace(/[._-]+/g, ' ');
      const from = str(detail['from']);
      const to = str(detail['to']);
      if (lc(e.eventType) === 'transition' && to) {
        what = from ? `${statusWords(from)} → ${statusWords(to)}` : statusWords(to);
        if (to === 'decided' && str(after['determination'])) what += `: ${determinationWords(str(after['determination']))}`;
      } else if (lc(e.eventType) === 'edited' && lc(str(after['complianceFlag'])) === 'agent-flagged') {
        what = 'Flagged for compliance review';
      } else if (lc(e.eventType) === 'link-added') {
        const t = str(detail['type']);
        what = `Link added: ${t ? linkWords(t) : 'link'} ${str(detail['label']) ?? str(detail['ref']) ?? ''}`.trim();
      }
      if (lc(e.eventType) === 'filed' && lc(str(after['complianceFlag'])) === 'agent-flagged') what += ' and flagged for compliance review';
      const note = str(detail['note']) ?? e.reason ?? null;
      return {
        id: e.eventId,
        when: whenText(e.occurredAt),
        who: personName(e.actor),
        what,
        note,
        viaPanel: (str(detail['via']) ?? '').startsWith('app:'),
      };
    });
}

// ── reviewing ────────────────────────────────────────────────────────────────────────────────────────────────

export interface ActionDef {
  readonly action: ReviewAction;
  readonly label: string;
  readonly help: string;
  readonly placeholder: string;
  readonly tone: 'primary' | 'ok' | 'neutral' | 'danger';
}

export const REVIEW_ACTIONS: ReadonlyArray<ActionDef> = [
  {
    action: 'confirm',
    label: 'Confirm impact',
    tone: 'primary',
    help: 'This change matters to compliance. Your decision and note are recorded on the item.',
    placeholder: 'Which rule or process it affects, and anything the team should do',
  },
  {
    action: 'clear',
    label: 'Clear',
    tone: 'ok',
    help: 'This change does not affect compliance. Your decision and note are recorded on the item.',
    placeholder: 'Why it does not affect compliance',
  },
  {
    action: 'note',
    label: 'Add note',
    tone: 'neutral',
    help: 'Adds a note to the item’s history without changing anything else.',
    placeholder: 'Your note',
  },
  {
    action: 'request-revert',
    label: 'Request undo',
    tone: 'danger',
    help: 'Asks the developers to undo this change in the live app. They receive an Innovation Hub request.',
    placeholder: 'Why it should be undone',
  },
];

/** An undo can be asked for only once the change is live (released or checked). */
export function canRequestRevert(row: Pick<ReviewRow, 'status'>): boolean {
  const st = lc(row.status);
  return st === 'deployed' || st === 'verified';
}

export function actionsFor(row: Pick<ReviewRow, 'status'>): ReadonlyArray<ActionDef> {
  return REVIEW_ACTIONS.filter((a) => a.action !== 'request-revert' || canRequestRevert(row));
}

/** The Hub keeps up to 1,000 characters of a review note (TrackerRules.ReviewNoteMax). */
export const NOTE_MAX = 1000;

/** The reviewers' names joined for a sentence: "Jessica Beucler and Ginger McCullough". */
export function namesSentence(people: ReadonlyArray<{ upn: string; name: string }>): string {
  const names = people.map((p) => personName(p.upn, p.name));
  if (names.length <= 1) return names[0] ?? 'nobody yet';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
